/// <reference path="../_references.ts"/>
module App.Appointments {

    interface HasHistoryData {
        addressId: number;
        serviceRequests: boolean;
        recommendations: boolean;
        files: boolean;
    }

    export class AppointmentMenuController{
        static $inject = ['$state', '$q', 'SelectedAppointment', 'PermissionService', 'HistoryService'];
        historyValues: HasHistoryData = null;

        constructor(
            private $state: ng.ui.IStateService,
            private $q: ng.IQService,
            private SelectedAppointment: SelectedAppointment,
            protected PermissionService: App.Helpers.PermissionService,
            private HistoryService: App.History.HistoryService
        ) {
            SelectedAppointment.addListener(this.setAppointment.bind(this));

            SelectedAppointment.getAppointment().then((appointment) => {
                this.setAppointment(appointment);
                this.historyValues = null;
            });

            this.setMenuOptions();
        }

        appointment: Appointment = null;

        menuOptions: MenuItem[] = [];
        baseMenuOptions: MenuItem[] = [
            {
                title: 'Home',
                uiSref: 'zipSolutions.home.appointment',
                permission: ['web.field.left-nav.overview', 'web.office.service-requests.service-request.tech-view'],
                displayKeys: true,
                action: (SelectedAppointment: SelectedAppointment): any => {
                    if (SelectedAppointment.isSet() === false){
                        this.appointment = null;
                        return Promise.resolve();
                    }
                    return SelectedAppointment.pauseAppointment().then(() => {
                        SelectedAppointment.clear();
                        return Promise.resolve();
                    });
                }
            },
            {
                title: 'History',
                titleTemplate: 'html/appointment/appointment-menu-history.tpl.html',
                uiSref: 'zipSolutions.history',
                permission: 'web.field.left-nav.history',
                displayKeys: ['Paused', 'Started', 'Arrived', 'Dispatched', 'Acknowledged']
            },
            {
                title: 'Recurring Maintenance',
                uiSref: 'zipSolutions.maintenance',
                permission: 'web.field.left-nav.item-management',
                displayKeys: ['Paused', 'Started', 'Arrived', 'Dispatched', 'Acknowledged']
            },
            {
                title: 'Purchase Order',
                uiSref: 'zipSolutions.administrative.purchase-order',
                permission: 'web.field.left-nav.purchase-order',
                displayKeys: ['Unselected', 'Started', 'Paused', 'Dispatched', 'Acknowledged'],
            },
            {
                title: 'Inventory',
                uiSref: 'zipSolutions.administrative.inventory',
                permission: 'web.field.left-nav.inventory',
                displayKeys: ['Started', 'Paused', 'Dispatched', 'Acknowledged'],
            },
            {
                title: 'Questionnaire',
                uiSref: 'zipSolutions.questionnaire.service',
                permission: 'web.field.left-nav.questionnaire',
                displayKeys: ['Paused', 'Started', 'Arrived', 'Dispatched', 'Acknowledged'],
                questionnaireIcon: true
            },
            {
                title: 'Price Guide',
                uiSref: 'zipSolutions.price-guide',
                displayKeys: ['Arrived'],
                permission: ['web.field.price-guide.view', 'web.office.service-requests.service-request.tech-view']
            },
            {
                title: 'Work Order',
                uiSref: 'zipSolutions.workOrder',
                displayKeys: ['Arrived'],
                permission: ['web.field.left-nav.work-order', 'web.office.service-requests.service-request.tech-view']
            },
            {
                title: 'Payment',
                uiSref: 'zipSolutions.payment',
                displayKeys: ['Arrived'],
                permission: ['web.field.left-nav.payment', 'web.office.service-requests.service-request.tech-view']
            },
            {
                title: 'Administrative',
                uiSref: 'zipSolutions.administrative',
                displayKeys: ['Arrived'],
                permission: 'web.field.left-nav.administrative'
            },
            {
                title: 'SR Forms',
                uiSref: 'zipSolutions.forms',
                displayKeys: ['Arrived'],
                permission: ['web.field.left-nav.service-request-forms', 'web.office.service-requests.service-request.tech-view']
            },
            {
                title: 'Inventory Replenish (new)',
                uiSref: 'zipSolutions.administrative.vendor-inventory',
                displayKeys: ['Arrived'],
                permission: 'web.field.administrative.vendor-inventory',
            },
        ] as MenuItem[];

        setAppointment(appointment: Appointment): void {
            this.appointment = appointment;
            this.setMenuOptions();
        }

        setMenuOptions(): void {
            this.menuOptions = this.baseMenuOptions.filter(option => {
                return !option.permission || (
                    angular.isArray(option.permission)
                        ? this.PermissionService.hasAnyPermission(option.permission)
                        : this.PermissionService.hasPermission(option.permission)
                );
            });
        }

        getDateTime(): string|null {
            if (!this.appointment) {
                return null;
            }

            return (new Date(
                this.appointment.scheduled_date + ' ' +
                this.appointment.scheduled_time
            )).toLocaleString();
        }

        hide(): void {
            const element = <HTMLElement>document.getElementsByClassName('mdl-layout__obfuscator')[0];

            element.click();
        }

        clickAction(item: MenuItem): void {
            const vm = this;
            if (angular.isDefined(item.action)) {
                item.loading = true;
                item.action(vm.SelectedAppointment).then(() => {
                    vm.$state.go(item.uiSref);
                    vm.hide();
                    item.loading = false;
                });
            }
            else {
                vm.$state.go(item.uiSref);
                vm.hide();
            }
        }

        getHistoryValues(item: MenuItem) {
            if (item.loading) {
                return;
            }

            const addrId = this.SelectedAppointment?.service_request?.service_address?.id;
            if (this.historyValues !== null) {
                if (this.historyValues.addressId === addrId) {
                    return this.historyValues;
                } else {
                    this.historyValues = null;
                }
            }

            item.loading = true;
            if (addrId === null) {
                this.historyValues = {
                    addressId: null,
                    serviceRequests: false,
                    recommendations: false,
                    files: false,
                };
                item.loading = false;
            } else {
                this.$q.all([
                    this.HistoryService.getServiceRequestHistoryByAddress(addrId, {size: 0}),
                    this.HistoryService.getServiceRequestRecommendationsByAddress(addrId, {size: 0}),
                    this.HistoryService.getServiceRequestPhotosByAddress(addrId, {size: 0}),
                ]).then((v) => {
                    this.historyValues = {
                        addressId: addrId,
                        serviceRequests: Number(v[0].headers('count')) > 0,
                        recommendations: Number(v[1].headers('count')) > 0,
                        files: Number(v[2].headers('count')) > 0,
                    };
                    item.loading = false;
                });
            }

            return this.historyValues;
        }
    }
}
